// src/pages/TenantManagement/modals/EditTenantModal.tsx
import React, { useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  FormFeedback,
  Row,
  Col,
  Alert,
} from 'reactstrap';
import { useFormik } from 'formik';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { useLocation } from 'react-router-dom';

// Redux
import { updateExistingTenant } from 'slices/tenantManagement/thunk';
import { closeModal, setSelectedTenant } from 'slices/tenantManagement/reducer';
import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';

// Validation
import { createTenantSchema } from '../validation';

// Types
import { Tenant } from '../types';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('tenant_management');

interface EditTenantModalProps {
  isOpen: boolean;
  toggle: (reload?: boolean) => void;
  tenant: Tenant | null;
  tenants: Tenant[]; // For parent selection
  managers: any[]; // List of users for manager selection
}

const EditTenantModal: React.FC<EditTenantModalProps> = ({
  isOpen,
  toggle,
  tenant,
  tenants,
  managers,
}) => {
  const dispatch = useDispatch<any>();
  const location = useLocation();

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      code: tenant?.code || '',
      name: tenant?.name || '',
      type: tenant?.type?.toString() || '1',
      managerId: tenant?.managerId?.toString() || '',
      description: tenant?.description || '',
      address: tenant?.address || '',
      phone: tenant?.phone || '',
      email: tenant?.email || '',
      parentId: tenant?.parentId?.toString() || '',
      status: tenant?.status?.toString() || '1',
    },
    validationSchema: createTenantSchema(true), // isEdit = true
    onSubmit: async (values) => {
      if (!tenant) return;
      
      try {
        await dispatch(updateExistingTenant({
          id: tenant.id,
          data: {
            id: tenant.id,
            code: tenant.code, // Code is read-only
            name: values.name,
            type: parseInt(values.type),
            managerId: values.managerId ? parseInt(values.managerId) : undefined,
            description: values.description || undefined,
            address: values.address || undefined,
            phone: values.phone || undefined,
            email: values.email || undefined,
            parentId: values.parentId ? parseInt(values.parentId) : undefined,
            status: values.status ? parseInt(values.status) : undefined,
          },
        })).unwrap();
        
                toast.success(st('cap_nhat_don_vi_thanh_cong_message'));
        validation.resetForm();
        toggle(true); // Reload list
      } catch (error: any) {
        toast.error(error || st('co_loi_xay_ra_khi_cap_message'));
      }
    },
  });

  const handleClose = () => {
    validation.resetForm();
    toggle();
  };

  if (!tenant) return null;

  // Filter out current tenant and its descendants from parent options
  const availableParents = tenants.filter(t => t.id !== tenant.id);

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered size="lg">
      <ModalHeader toggle={handleClose}>
        <i className="ri-edit-line me-2"></i>
        {st('chinh_sua_don_vi_title')}
      </ModalHeader>
      <Form onSubmit={validation.handleSubmit}>
        <ModalBody>
          <Row>
            {/* Code - Read-only */}
            <Col md={6}>
              <FormGroup>
                <Label for="code">{st('ma_don_vi_label')}</Label>
                <Input
                  type="text"
                  id="code"
                  name="code"
                  value={validation.values.code}
                  data-action-code="edit-input-code"
                  data-action-name="Mã đơn vị (Sửa)"
                  disabled
                  className="bg-light"
                />
                <small className="text-muted">
                  {st('ma_khong_the_thay_doi_sau_hint')}
                </small>
              </FormGroup>
            </Col>

                        {/* Type */}
                        <Col md={6}>
                          <FormGroup>
                            <Label for="type">{st('loai_don_vi_label')}</Label>
                            <select
                              id="type"
                              name="type"
                              value={validation.values.type || '1'}
                              data-action-code="edit-input-type"
                              data-action-name="Loại đơn vị (Sửa)"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              disabled={!PageActionPermission.canEnable(location.pathname, "edit-input-type")}
                              className={`form-select ${validation.touched.type && validation.errors.type ? 'is-invalid' : ''}`}
                            >
                              <option value="1">{st('phong_ban_option')}</option>
                              <option value="2">{st('chi_nhanh_option')}</option>
                            </select>
                            {validation.touched.type && validation.errors.type ? (
                              <div className="invalid-feedback d-block">
                                {validation.errors.type}
                              </div>
                            ) : null}
                          </FormGroup>
                        </Col>

            {/* Name */}
            <Col md={12}>
              <FormGroup>
                <Label for="name">{st('ten_don_vi_label')}</Label>
                <Input
                  type="text"
                  id="name"
                  name="name"
                  placeholder={st('nhap_ten_day_du_cua_don_placeholder')}
                  maxLength={200}
                  value={validation.values.name}
                  data-action-code="edit-input-name"
                  data-action-name="Tên đơn vị (Sửa)"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  disabled={!PageActionPermission.canEnable(location.pathname, "edit-input-name")}
                  invalid={
                    validation.touched.name && validation.errors.name
                      ? true
                      : false
                  }
                />
                {validation.touched.name && validation.errors.name ? (
                  <FormFeedback type="invalid">
                    {validation.errors.name}
                  </FormFeedback>
                ) : null}
              </FormGroup>
            </Col>

                        {/* Manager */}
                        <Col md={6}>
                          <FormGroup>
                            <Label for="managerId">{st('quan_ly_text')}</Label>
                            <select
                              id="managerId"
                              name="managerId"
                              value={validation.values.managerId || ''}
                              data-action-code="edit-input-manager"
                              data-action-name="Quản lý (Sửa)"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              disabled={!PageActionPermission.canEnable(location.pathname, "edit-input-manager")}
                              className="form-select"
                            >
                              <option value="">{st('chon_quan_ly_option')}</option>
                              {managers.map((manager) => (
                                <option key={manager.id} value={manager.id}>
                                  {manager.name}
                                </option>
                              ))}
                            </select>
                          </FormGroup>
                        </Col>

                        {/* Parent Tenant */}
                        <Col md={6}>
                          <FormGroup>
                            <Label for="parentId">{st('don_vi_cha_label')}</Label>
                            <select
                              id="parentId"
                              name="parentId"
                              value={validation.values.parentId || ''}
                              data-action-code="edit-input-parent"
                              data-action-name="Đơn vị cha (Sửa)"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              disabled={!PageActionPermission.canEnable(location.pathname, "edit-input-parent")}
                              className="form-select"
                            >
                              <option value="">{st('khong_co_option')}</option>
                              {availableParents.map((t) => (
                                <option key={t.id} value={t.id}>
                                  {t.name}
                                </option>
                              ))}
                            </select>
                          </FormGroup>
                        </Col>

            {/* Description */}
            <Col md={12}>
              <FormGroup>
                <Label for="description">{st('mo_ta_label')}</Label>
                <Input
                  type="textarea"
                  id="description"
                  name="description"
                  placeholder={st('mo_ta_ve_chuc_nang_va_placeholder')}
                  rows={3}
                  maxLength={500}
                  value={validation.values.description}
                  data-action-code="edit-input-description"
                  data-action-name="Mô tả đơn vị (Sửa)"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  disabled={!PageActionPermission.canEnable(location.pathname, "edit-input-description")}
                  invalid={
                    validation.touched.description &&
                    validation.errors.description
                      ? true
                      : false
                  }
                />
                {validation.touched.description &&
                validation.errors.description ? (
                  <FormFeedback type="invalid">
                    {validation.errors.description}
                  </FormFeedback>
                ) : null}
              </FormGroup>
            </Col>

            {/* Address */}
            <Col md={12}>
              <FormGroup>
                <Label for="address">
                  {st('dia_chi_label')} {validation.values.type === '2' && <span className="text-danger">*</span>}
                </Label>
                <Input
                  type="text"
                  id="address"
                  name="address"
                  placeholder={st('nhap_dia_chi_van_phong_placeholder')}
                  maxLength={255}
                  value={validation.values.address}
                  data-action-code="edit-input-address"
                  data-action-name="Địa chỉ đơn vị (Sửa)"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  disabled={!PageActionPermission.canEnable(location.pathname, "edit-input-address")}
                  invalid={
                    validation.touched.address && validation.errors.address
                      ? true
                      : false
                  }
                />
                {validation.touched.address && validation.errors.address ? (
                  <FormFeedback type="invalid">
                    {validation.errors.address}
                  </FormFeedback>
                ) : null}
              </FormGroup>
            </Col>

            {/* Phone */}
            <Col md={6}>
              <FormGroup>
                <Label for="phone">{st('so_dien_thoai_label')}</Label>
                <Input
                  type="tel"
                  id="phone"
                  name="phone"
                  placeholder={st('vd_0901234567_placeholder')}
                  maxLength={11}
                  value={validation.values.phone}
                  data-action-code="edit-input-phone"
                  data-action-name="Số điện thoại đơn vị (Sửa)"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  disabled={!PageActionPermission.canEnable(location.pathname, "edit-input-phone")}
                  invalid={
                    validation.touched.phone && validation.errors.phone
                      ? true
                      : false
                  }
                />
                {validation.touched.phone && validation.errors.phone ? (
                  <FormFeedback type="invalid">
                    {validation.errors.phone}
                  </FormFeedback>
                ) : null}
              </FormGroup>
            </Col>

            {/* Email */}
            <Col md={6}>
              <FormGroup>
                <Label for="email">{st('email_label')}</Label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  placeholder={st('vd_dept_company_com_placeholder')}
                  maxLength={100}
                  value={validation.values.email}
                  data-action-code="edit-input-email"
                  data-action-name="Email đơn vị (Sửa)"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  disabled={!PageActionPermission.canEnable(location.pathname, "edit-input-email")}
                  invalid={
                    validation.touched.email && validation.errors.email
                      ? true
                      : false
                  }
                />
                {validation.touched.email && validation.errors.email ? (
                  <FormFeedback type="invalid">
                    {validation.errors.email}
                  </FormFeedback>
                ) : null}
              </FormGroup>
            </Col>

                        {/* Status */}
                        <Col md={6}>
                          <Label for="status">{st('trang_thai_label')}</Label>
                          <select
                            id="status"
                            name="status"
                            value={validation.values.status || '1'}
                            data-action-code="edit-input-status"
                            data-action-name="Trạng thái đơn vị (Sửa)"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            disabled={!PageActionPermission.canEnable(location.pathname, "edit-input-status")}
                            className="form-select"
                          >
                            <option value="1">{st('hoat_dong_option')}</option>
                            <option value="0">{st('ngung_hoat_dong_option')}</option>
                          </select>
                        </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={handleClose}>
            <i className="ri-close-line me-1"></i>
            {st('huy_bo_button')}
          </Button>
          {PageActionPermission.canDisplay(location.pathname, "button-edit-submit") && (
          <Button
            color="primary"
            type="submit"
            data-action-code="button-edit-submit"
            data-action-name="Lưu thay đổi đơn vị"
          >
            <i className="ri-save-line me-1"></i>
            {st('cap_nhat_button')}
          </Button>
          )}
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default EditTenantModal;